import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://verithos.co';

interface SEOHeadProps {
  title?: string;
  description?: string;
  ogImage?: string;
}

// Default metadata for pages - optimized for search rankings
const pageMetadata: Record<string, { title: string; description: string; keywords?: string }> = {
  '/': {
    title: 'Verithos | AI Qualitative Research Co-Pilot for Defensible Findings',
    description: 'Produce defensible qualitative findings with traceable evidence. AI-assisted transcript coding for Grounded Theory, Thematic Analysis, Phenomenology. Quote-linked themes, saturation tracking, 19 citation styles.',
    keywords: 'qualitative research software, AI transcript coding, grounded theory software, thematic analysis tool, phenomenology research, qualitative data analysis, NVivo alternative, ATLAS.ti alternative, interview analysis software, research methodology tool'
  },
  '/auth': {
    title: 'Sign In or Sign Up | Verithos',
    description: 'Create your Verithos account or sign in to access your qualitative research projects. Free to get started, no credit card required.',
    keywords: 'verithos login, verithos sign up, qualitative research account'
  },
  '/pricing': {
    title: 'Pricing | Verithos — Transparent Plans for Qualitative Researchers',
    description: 'Simple pricing for rigorous research. Free tier to Pro plans with unlimited transcripts, saturation tracking, and 19 citation styles. 14-day money-back guarantee.',
    keywords: 'qualitative research pricing, research software cost, academic research tools pricing, transcript analysis software'
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Verithos — Your Research Data Protected',
    description: 'Your research data is encrypted, never used for AI training, and fully under your control. AES-256 encryption at rest, TLS 1.2+ in transit.',
    keywords: 'research data privacy, data protection research software, verithos privacy'
  },
  '/terms-of-service': {
    title: 'Terms of Service | Verithos — Fair Terms for Researchers',
    description: 'Clear, fair terms for using Verithos. 14-day refund policy, transparent data handling, researcher-first policies.',
  },
  '/what-is-verithos': {
    title: 'What is Verithos? | AI Qualitative Research Co-Pilot for Traceable Evidence',
    description: 'Verithos is an AI Qualitative Research Co-Pilot that helps researchers produce defensible qualitative findings with traceable evidence. Every claim links to source quotes.',
    keywords: 'what is verithos, AI qualitative research, defensible findings, traceable evidence, qualitative analysis co-pilot'
  },
  '/sample-report': {
    title: 'Sample Report | Verithos — See What We Produce',
    description: 'View an example qualitative findings report generated with Verithos. Demonstrates structure, tone, and evidence linking. Interpretation remains the researcher\'s responsibility.',
    keywords: 'qualitative research report example, thematic analysis report, research findings sample, evidence-based research output'
  },
  '/how-to-cite': {
    title: 'How to Cite Verithos | Disclosure Guidance for Qualitative Research',
    description: 'Clear guidance on disclosing and citing the use of Verithos in qualitative research. Aligned with ethics review and publication norms.',
    keywords: 'cite AI research tool, qualitative research disclosure, AI ethics disclosure, research transparency, methodology section AI'
  },
  '/verithos-vs-llms': {
    title: 'Verithos vs ChatGPT, Gemini, DeepSeek | Built for research workflows',
    description: 'Public LLMs are great for drafting and brainstorming. Verithos is built for qualitative research workflows with traceable evidence, repeatability, and export-ready outputs.',
    keywords: 'verithos vs chatgpt, qualitative research AI, chatgpt research limitations, AI research workflow, evidence traceability, defensible qualitative findings'
  },
  '/resources': {
    title: 'Resources | Verithos — Proof, guidance, and research integrity',
    description: "Explore demos, sample outputs, citation guidance, and Verithos' evidence-first approach to qualitative research. Built for traceability and defensible findings.",
    keywords: 'qualitative research resources, cite AI in research, research integrity AI, traceable evidence, Verithos sample report'
  },
  '/verithos-vs-nvivo-atlasti-maxqda-dedoose': {
    title: 'Verithos vs NVivo vs ATLAS.ti vs MAXQDA vs Dedoose – QDAS Comparison 2026',
    description: 'Compare Verithos (AI-native qualitative analysis) with NVivo, ATLAS.ti, MAXQDA, and Dedoose. See differences in first-pass speed, traceability, methodology support, pricing, deployment, and best use cases for academics, PhD students, and market researchers in 2026.',
    keywords: 'Verithos vs NVivo, Verithos vs ATLAS.ti, best AI qualitative data analysis software 2026, QDAS comparison 2026, Verithos vs MAXQDA vs Dedoose, AI-powered QDAS for interviews, qualitative data analysis software comparison'
  },
};

export function SEOHead({ title, description, ogImage }: SEOHeadProps) {
  const location = useLocation();
  const rawPathname = location.pathname;
  
  // Normalize pathname: strip trailing slash for consistent matching
  const pathname = rawPathname === '/' ? '/' : rawPathname.replace(/\/$/, '');
  
  // Share-safe .html slugs — canonical must match sitemap entries
  const htmlSlugs: Record<string, string> = {
    '/pricing': 'pricing.html',
    '/what-is-verithos': 'what-is-verithos.html',
    '/sample-report': 'sample-report.html',
    '/how-to-cite': 'how-to-cite.html',
    '/privacy-policy': 'privacy-policy.html',
    '/terms-of-service': 'terms-of-service.html',
    '/verithos-vs-llms': 'verithos-vs-llms.html',
    '/whitepaper': 'whitepaper.html',
    '/whitepaper/workflow': 'whitepaper-workflow.html',
    '/whitepaper/standards': 'whitepaper-standards.html',
    '/whitepaper/references': 'whitepaper-references.html',
    '/whitepaper/changelog': 'whitepaper-changelog.html',
    '/resources': 'resources.html',
    '/verithos-vs-nvivo-atlasti-maxqda-dedoose': 'verithos-vs-nvivo-atlasti-maxqda-dedoose.html',
  };

  // Canonical URL — use .html for public pages to match sitemap
  const canonicalUrl = pathname === '/'
    ? `${BASE_URL}/`
    : htmlSlugs[pathname]
      ? `${BASE_URL}/${htmlSlugs[pathname]}`
      : `${BASE_URL}${pathname}`;
  
  // Noindex pages that should not appear in search results
  // Includes auth and all authenticated app routes that show spinners/redirects to crawlers
  const noIndexRoutes = [
    '/auth',
    '/projects',
    '/dashboard',
    '/transcripts',
    '/codebook',
    '/findings',
    '/draft',
    '/settings',
    '/inbox',
    '/research-advisor',
    '/onboarding',
    '/onboarding/start',
    '/admin',
    '/checkout/success',
  ];
  const isNoIndex = noIndexRoutes.includes(pathname) || pathname.startsWith('/project/');
  
  // Get page-specific or custom metadata
  const pageMeta = pageMetadata[pathname] || pageMetadata['/'];
  const pageTitle = title || pageMeta.title;
  const pageDescription = description || pageMeta.description;
  const pageOgImage = ogImage || `${BASE_URL}/Verithos-OG-V2.png`;
  const pageKeywords = pageMeta.keywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {pageKeywords && <meta name="keywords" content={pageKeywords} />}
      
      {/* Robots — noindex for auth, default index for everything else */}
      {isNoIndex && <meta name="robots" content="noindex,follow" />}
      
      {/* Canonical URL - Route Specific */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language */}
      <html lang="en" />
      <meta httpEquiv="content-language" content="en" />
      
      {/* Open Graph / Facebook - Route Specific */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Verithos - Your AI Qualitative Research Co-Pilot" />
      <meta property="og:site_name" content="Verithos" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@VerithosHQ" />
      <meta name="twitter:creator" content="@VerithosHQ" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageOgImage} />
      <meta name="twitter:image:alt" content="Verithos - Your AI Qualitative Research Co-Pilot" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content="Verithos" />
      <meta name="publisher" content="Verithos" />
      <meta name="application-name" content="Verithos" />
      
      {/* Favicons - URL-based, not base64 */}
      <link rel="icon" href="https://verithos.co/favicon.ico?v=4" sizes="any" />
      <link rel="icon" type="image/png" sizes="48x48" href="https://verithos.co/favicon-48.png?v=4" />
      <link rel="icon" type="image/png" sizes="64x64" href="https://verithos.co/favicon-64.png?v=4" />
      <link rel="apple-touch-icon" sizes="180x180" href="https://verithos.co/apple-touch-icon.png?v=4" />
      <link rel="manifest" href="/site.webmanifest" />
    </Helmet>
  );
}
